import { Fragment, type ComponentType, type ReactNode } from 'react';
import { Skeleton, cn } from '@clarix/ui';

interface DetailRowProps {
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  label: string;
  value: ReactNode;
}

export function DetailRow({ icon: Icon, label, value }: DetailRowProps) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <span className="text-muted-foreground bg-surface flex h-8 w-8 flex-none items-center justify-center rounded-md">
        <Icon className="h-4 w-4" strokeWidth={1.5} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-muted-foreground text-sm font-medium uppercase tracking-wide">{label}</p>
        <div className="text-foreground truncate text-xl">{value}</div>
      </div>
    </div>
  );
}

export function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-border bg-background rounded-xl border p-5">
      <h2 className="text-foreground mb-2 text-xl font-semibold">{title}</h2>
      {children}
    </section>
  );
}

interface ProfileColumnProps {
  id: string;
  /** At `xl` the last card grows so column bottoms line up; the aside opts out so short cards stay short. */
  grow?: boolean;
  children: ReactNode;
}

function ProfileColumn({ id, grow = false, children }: ProfileColumnProps) {
  return (
    <div
      data-profile-column={id}
      className={cn('flex min-w-0 flex-col gap-4', grow && 'xl:[&>*:last-child]:flex-1')}
    >
      {children}
    </div>
  );
}

interface ProfileShellProps {
  /** Left column — identity plus reporting line. */
  aside: ReactNode;
  /** Right-hand card stacks; independent stacks avoid row-height holes. */
  columns: ReactNode[];
  /** Full-width block under the card stacks, for content that needs the room. */
  footer?: ReactNode;
}

/** Full-width profile layout: aside plus one or two card stacks; one column below `lg`. */
export function ProfileShell({ aside, columns, footer }: ProfileShellProps) {
  return (
    <div className="grid w-full gap-4 p-5 lg:grid-cols-[minmax(280px,340px)_1fr]">
      <ProfileColumn id="aside">{aside}</ProfileColumn>
      <div className={cn('grid min-w-0 gap-4', columns.length > 1 && 'xl:grid-cols-2')}>
        {columns.map((column, i) => (
          <ProfileColumn key={i} id={String(i)} grow>
            {column}
          </ProfileColumn>
        ))}
        {footer && (
          <div
            data-profile-column="footer"
            className={cn('min-w-0', columns.length > 1 && 'xl:col-span-2')}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

interface ProfileSkeletonProps {
  /** Number of right-hand card stacks (1 or 2). */
  columns?: 1 | 2;
}

// `min-h-*`, not `h-*`: the growing last block has `flex-basis: 0`, which would collapse a fixed height.
const SKELETON_STACKS = [
  ['min-h-56', 'min-h-80'],
  ['min-h-32', 'min-h-96'],
] as const;

/** Mirrors `ProfileShell`: identity + reports-to on the left, card stacks on the right. */
export function ProfileSkeleton({ columns = 2 }: ProfileSkeletonProps) {
  return (
    <ProfileShell
      aside={
        <>
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-32 w-full rounded-xl" />
        </>
      }
      columns={SKELETON_STACKS.slice(0, columns).map((heights) => (
        <Fragment key={heights.join()}>
          {heights.map((height) => (
            <Skeleton key={height} className={cn('w-full rounded-xl', height)} />
          ))}
        </Fragment>
      ))}
    />
  );
}
